import {Heart,MapPin,X} from "lucide-react";
import {useMutation,useQuery,useQueryClient} from "@tanstack/react-query";
import {useRef,useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {api,json} from "../api";
import {useLocale} from "../App";
import {selectDiscoveryPhoto} from "../discovery-photo";
import type {Woman} from "../types";
import {BottomNav,Distance,PhoneShell} from "../components/Shell";

const discoverySeed=()=>crypto.randomUUID?.()??`${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function NearbyPage(){
  const lang=useLocale();const {t}=useTranslation();const navigate=useNavigate();const queryClient=useQueryClient();
  const roundSeed=useRef(discoverySeed());
  const pointerStart=useRef(0);const dragX=useRef(0);const dragged=useRef(false);const [drag,setDrag]=useState({x:0,active:false});
  const women=useQuery({queryKey:["women",lang],queryFn:async()=>{
    const load=()=>api<{women:Woman[];total:number}>(`/women?lang=${lang}&seed=${roundSeed.current}`);
    let result=await load();
    if(result.women.length===0&&result.total>0){
      await api("/women/restart",{method:"POST"});
      roundSeed.current=discoverySeed();
      result=await load();
    }
    return result;
  }});
  const woman=women.data?.women[0];
  const photoUrl=woman?selectDiscoveryPhoto(woman.photos)??"/dating-v1/seed/fallback.webp":"/dating-v1/seed/fallback.webp";
  const removeFromCardStack=async(id:string)=>{
    await queryClient.cancelQueries({queryKey:["women",lang]});
    queryClient.setQueryData<{women:Woman[];total:number}>(["women",lang],current=>current?{...current,women:current.women.filter(item=>item.id!==id)}:current);
  };
  const skip=useMutation({mutationFn:(id:string)=>api(`/women/${id}/skip`,{method:"POST"}),onMutate:removeFromCardStack,onSettled:()=>queryClient.invalidateQueries({queryKey:["women",lang]})});
  const like=useMutation({
    mutationFn:(id:string)=>api<{conversationId:string}>(`/likes/${id}`,{method:"POST"}),
    onMutate:removeFromCardStack,
    onSuccess:(result,id)=>navigate(`/${lang}/match/${id}/${result.conversationId}`),
    onSettled:()=>queryClient.invalidateQueries({queryKey:["women",lang]}),
  });
  const decide=(direction:"left"|"right")=>{if(!woman||skip.isPending||like.isPending)return;const selected=woman;setDrag({x:direction==="right"?520:-520,active:false});window.setTimeout(()=>{if(direction==="right")like.mutate(selected.id);else skip.mutate(selected.id);setDrag({x:0,active:false});},220)};
  const pointerDown=(event:React.PointerEvent<HTMLElement>)=>{pointerStart.current=event.clientX;dragX.current=0;dragged.current=false;event.currentTarget.setPointerCapture(event.pointerId);setDrag({x:0,active:true});};
  const pointerMove=(event:React.PointerEvent<HTMLElement>)=>{if(!drag.active)return;const x=event.clientX-pointerStart.current;dragX.current=x;if(Math.abs(x)>5)dragged.current=true;setDrag({x,active:true});};
  const pointerUp=()=>{if(!drag.active)return;const x=dragX.current;dragX.current=0;if(Math.abs(x)>=90)decide(x>0?"right":"left");else setDrag({x:0,active:false});};
  return <PhoneShell className="nearby-page">
    <header className="nearby-header"><h1>{t("nearby")}</h1><span className="online-count"><i/>1,861</span></header>
    <section className="nearby-body">
      {(women.isLoading||(women.isFetching&&!woman))&&<div className="profile-card skeleton discovery-refresh"><MapPin/><span>{t("refreshingNearby")}</span></div>}
      {!women.isLoading&&!women.isFetching&&!woman&&<div className="empty-state"><MapPin/><h2>{t("noMore")}</h2><button onClick={()=>queryClient.invalidateQueries({queryKey:["women",lang]})}>{t("refresh")}</button></div>}
      {woman&&<>
        <article data-woman-id={woman.id} className={`profile-card swipe-card ${drag.active?"dragging":""}`} style={{transform:`translateX(${drag.x}px) rotate(${drag.x/24}deg)`}} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp} onClick={()=>{if(!dragged.current)navigate(`/${lang}/women/${woman.id}`)}}>
          <div className="profile-photo-frame">
            <img className="profile-photo-main" src={photoUrl} alt={woman.name} draggable={false}/>
          </div>
          <div className="swipe-stamp like-stamp" style={{opacity:Math.max(0,Math.min(1,drag.x/80))}}>{t("like").toUpperCase()}</div><div className="swipe-stamp skip-stamp" style={{opacity:Math.max(0,Math.min(1,-drag.x/80))}}>{t("skip").toUpperCase()}</div>
          <div className="profile-overlay">
            <h2>{woman.name} <b>{woman.age}</b><i>✓</i></h2>
            <div className="profile-meta-row">
              <span className="profile-place"><MapPin aria-hidden="true"/><span>{woman.city} · {woman.district}</span></span>
              <span className="profile-distance"><MapPin aria-hidden="true"/><Distance meters={woman.distance_m}/></span>
            </div>
            <div className="interest-chips">{woman.interests.slice(0,3).map(item=><span key={item}>{item}</span>)}</div>
          </div>
        </article>
        <p className="swipe-hint">←　{t("swipeHint")}　→</p>
        <div className="discovery-actions"><button className="round-action skip" onClick={()=>decide("left")}><X/><span>{t("skip")}</span></button><button className="round-action like" onClick={()=>decide("right")}><Heart fill="currentColor"/><span>{t("like")}</span></button></div>
      </>}
    </section>
    <BottomNav/>
  </PhoneShell>;
}
